import {Children, createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Dialog, DialogBackdrop, DialogPanel} from '@headlessui/react'
import ModalHeader from './ModalHeader.jsx'

export const ModalFooterContext = createContext(null)

// flushBody: opt-out of the body's own desktop gutters/bottom padding for content
// that brings its own spacing (e.g. the Terminal guide's modalPages-spacing).
// flushTop:  opt-out of the top padding a headerless modal gets. Separate from
// flushBody because content can want its own side gutters while still needing that
// top gap — e.g. content whose FIRST element is its own header (the recommended-pickup
// screens render a ModalHeader inside the body).
// centered: keep the sheet in the MIDDLE of the screen on a phone too (default is a bottom sheet) —
// for floating cards like the booking-received deck, which isn't a full-width sheet.
// flushX goes one step further than flushBody: flushBody keeps `max-lg:px-4` so most
// flush content still clears the screen edge on a phone, but a full-bleed surface — the
// booking-received card deck, whose slides ARE the modal width — needs zero side padding
// at every width. Separate prop so the existing flushBody callers are untouched.
export default function Modal({isOpen, onClose, className, footer, children, scrollToTopOn, flushBody = false, flushTop = false, flushX = false, centered = false}) {
    // Cache the last-visible content so it stays rendered during the close animation,
    // even if the parent has already cleared the state that produced it.
    //
    // ⚠️ THE FLUSH FLAGS ARE CACHED WITH IT, and leaving them out was a visible bug. A
    // caller that sets them PER VIEW — `flushX={walkView === 'map'}` — clears that state in
    // the same click that closes the sheet, so the padding came back while the panel was
    // still on screen: a full-bleed map jumped inside white gutters for the length of the
    // 300ms slide out. Whatever is being animated away has to keep the layout it was drawn
    // with, not the layout of the state that replaced it.
    const live = {children, footer, className, flushBody, flushTop, flushX}
    const cache = useRef(live)
    if (isOpen) cache.current = live
    // Read as ONE object rather than a field at a time: everything the closing panel is
    // drawn from has to come from the same snapshot, and taking them together is what
    // makes that true by construction instead of by six matching ternaries.
    const {
        children: effChildren,
        footer: effFooter,
        className: effClassName,
        flushBody: effFlushBody,
        flushTop: effFlushTop,
        flushX: effFlushX,
    } = isOpen ? live : cache.current

    const [slotFooter, setSlotFooter] = useState(null)
    const bodyRef = useRef(null)
    const scrollToBottom = useCallback(() => {
        const el = bodyRef.current
        if (!el) return
        requestAnimationFrame(() => {
            el.scrollTo({top: el.scrollHeight, behavior: 'smooth'})
        })
    }, [])
    const scrollToTop = useCallback(() => {
        const el = bodyRef.current
        if (!el) return
        requestAnimationFrame(() => {
            el.scrollTo({top: 0, behavior: 'smooth'})
        })
    }, [])
    const ctxValue = useMemo(() => ({setFooter: setSlotFooter, scrollToBottom, scrollToTop}), [scrollToBottom, scrollToTop])
    const resolvedFooter = effFooter ?? slotFooter

    useEffect(() => {
        if (scrollToTopOn) scrollToTop()
    }, [scrollToTopOn, scrollToTop])

    const childArray = Children.toArray(effChildren)
    const firstChild = childArray[0]
    const hasHeader = firstChild?.type === ModalHeader
    const header = hasHeader ? firstChild : null
    const body = hasHeader ? childArray.slice(1) : childArray

    return (
        <Dialog open={isOpen} onClose={onClose} transition className="relative z-99999">
            <DialogBackdrop transition className="fixed inset-0 bg-[#0000005c] backdrop-blur-[10px] transition duration-200 ease-out data-closed:opacity-0"/>
            <div className={`fixed inset-0 flex items-center justify-center overflow-y-auto ${centered ? '' : 'max-md:items-end'}`}>
                {/* Entrance mirrors the booking modal (.hopn-modal-container): on mobile
                    the sheet slides up from the bottom (translate-y-full → 0); on desktop
                    it keeps the subtle scale/fade of a centred dialog. */}
                <DialogPanel transition className={`relative w-full max-w-200 max-h-[90dvh] flex flex-col overflow-hidden rounded-4xl bg-white transition ease-out duration-300 data-closed:opacity-0 md:data-closed:scale-95 max-md:data-closed:translate-y-full max-md:animate-sheet-up max-md:rounded-b-none max-md:max-h-[95dvh] max-md:max-w-full ${effClassName || ''} max-md:mb-0`}>
                    <ModalFooterContext.Provider value={ctxValue}>
                        {header && <div className="px-6 lg:px-8 pt-6 lg:pt-8 shrink-0">{header}</div>}
                        <div ref={bodyRef} className={`hopn-modal-body flex-1 overflow-y-auto overflow-x-hidden ${effFlushX ? '' : effFlushBody ? 'max-lg:px-4' : 'px-8 max-lg:px-4'} ${!header && !effFlushTop ? 'pt-6 lg:pt-8' : ''} ${!resolvedFooter && !effFlushBody ? 'pb-6 lg:pb-8' : ''}`}>
                            {body}
                        </div>
                        {resolvedFooter && <div className="px-6 lg:px-8 pb-6 lg:pb-8 pt-4 shrink-0">{resolvedFooter}</div>}
                    </ModalFooterContext.Provider>
                </DialogPanel>
            </div>
        </Dialog>
    )
}
